import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { VendedorService } from '../../../../../shared/services/apis/pessoa/vendedor.service';
import { VendedorResponsavelEmMassaRequest } from '../../../../../shared/services/requests/pessoa/vendedor-responsavel-em-massa.request';
import { VendedorHierarquiaStruct } from '../../../../../shared/services/structs/pessoa/vendedor-hierarquia.struct';
import { VendedorHierarquiaHelper } from '../../../../../shared/helpers/vendedor-hierarquia.helper';
import { SituacaoVendedorEnum } from '../../../../../shared/enums/pessoa/situacao-vendedor.enum';

export interface AlterarVendedorResponsavelMassaDialogData {
  idsVendedores: number[];
  nomesVendedores: string[];
}

interface OpcaoNovoResponsavel {
  idVendedor: number;
  nome: string;
  motivo: string;
  disabled: boolean;
}

interface LinhaImpacto {
  icone: string;
  texto: string;
  semAlteracao?: boolean;
}

// Story, seção 7: modal "Alterar vendedor responsável" (ação em massa da listagem).
@Component({
  selector: 'app-alterar-vendedor-responsavel-massa-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SelectPesquisavelComponent,
  ],
  templateUrl: './alterar-vendedor-responsavel-massa-dialog.component.html',
  styleUrl: './alterar-vendedor-responsavel-massa-dialog.component.css',
})
export class AlterarVendedorResponsavelMassaDialogComponent implements OnInit {
  public isLoading = false;
  public novoResponsavelControl = new FormControl<number | null>(null);
  public flatHierarquia: VendedorHierarquiaStruct[] = [];
  public opcoesNovoResponsavel: OpcaoNovoResponsavel[] = [];
  public impactoPreview: LinhaImpacto[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AlterarVendedorResponsavelMassaDialogData,
    private dialogRef: MatDialogRef<AlterarVendedorResponsavelMassaDialogComponent>,
    private vendedorService: VendedorService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.vendedorService.getHierarquiaFlat().subscribe(res => {
      if (res.isError) return;
      this.flatHierarquia = res.lista;
      this.montarOpcoesNovoResponsavel();
      this.recalcularImpacto();
    });

    this.novoResponsavelControl.valueChanges.subscribe(() => this.recalcularImpacto());
  }

  private get porId(): Map<number, VendedorHierarquiaStruct> {
    return new Map(this.flatHierarquia.map(v => [v.idVendedor, v]));
  }

  // Story, seção 7.1: vendedores da seleção e seus subordinados ficam indisponíveis como destino
  // (escolhê-los criaria ciclo). Marcados como desabilitados na lista, não removidos dela.
  private montarOpcoesNovoResponsavel(): void {
    const selecionados = new Set(this.data.idsVendedores);
    const descendentesDaSelecao = new Set<number>();
    for (const id of this.data.idsVendedores) {
      for (const d of VendedorHierarquiaHelper.getDescendentes(id, this.flatHierarquia)) {
        descendentesDaSelecao.add(d);
      }
    }

    // Inativos não entram como opção de novo responsável (Story: BUG 8 — "deve listar vendedores ativos").
    this.opcoesNovoResponsavel = this.flatHierarquia
      .filter(v => v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo)
      .map(v => {
        if (selecionados.has(v.idVendedor)) {
          return { idVendedor: v.idVendedor, nome: v.nome, disabled: true, motivo: 'Indisponível — está na seleção' };
        }
        if (descendentesDaSelecao.has(v.idVendedor)) {
          return { idVendedor: v.idVendedor, nome: v.nome, disabled: true, motivo: 'Indisponível — subordinado de um vendedor selecionado, criaria ciclo' };
        }
        return { idVendedor: v.idVendedor, nome: v.nome, disabled: false, motivo: '' };
      })
      .sort((a, b) => a.nome.localeCompare(b.nome));
  }

  // Story, seção 7.1: bloco "O que acontece ao confirmar" — uma linha por vendedor selecionado,
  // mesmas mensagens do backend (AlterarVendedorResponsavelEmMassa) e do bloco Equipe do cadastro.
  private recalcularImpacto(): void {
    const porId = this.porId;
    const novoId = this.novoResponsavelControl.value ?? null;
    const novoResponsavel = novoId != null ? porId.get(novoId) : null;
    const linhas: LinhaImpacto[] = [];

    for (const id of this.data.idsVendedores) {
      const vendedor = porId.get(id);
      if (!vendedor) continue;

      const responsavelAtualId = vendedor.idVendedorResponsavel ?? null;
      const responsavelAtual = responsavelAtualId != null ? porId.get(responsavelAtualId) : null;

      if (responsavelAtualId === novoId) {
        linhas.push({ icone: 'remove', texto: `${vendedor.nome} não muda — já está nesta condição.`, semAlteracao: true });
      } else if (novoId != null) {
        linhas.push(responsavelAtual
          ? { icone: 'swap_horiz', texto: `${vendedor.nome} deixa a equipe de ${responsavelAtual.nome} e passa a responder por ${novoResponsavel?.nome ?? '—'}.` }
          : { icone: 'person_add', texto: `${vendedor.nome} passa a responder por ${novoResponsavel?.nome ?? '—'}.` });
      } else {
        linhas.push({ icone: 'person_remove', texto: `${vendedor.nome} deixa a equipe de ${responsavelAtual?.nome ?? '—'} e fica sem vendedor responsável.` });
      }

      const qtdSubordinados = VendedorHierarquiaHelper.getQtdSubordinadosDiretos(id, this.flatHierarquia);
      if (qtdSubordinados > 0) {
        linhas.push({ icone: 'account_tree', texto: `${qtdSubordinados} subordinado(s) diretos de ${vendedor.nome} acompanham a mudança automaticamente.` });
      }
    }

    this.impactoPreview = linhas;
  }

  public confirmar(): void {
    if (this.isLoading) return;

    const request: VendedorResponsavelEmMassaRequest = {
      idsVendedores: this.data.idsVendedores,
      novoIdVendedorResponsavel: this.novoResponsavelControl.value ?? undefined,
    };

    this.isLoading = true;
    this.vendedorService.alterarVendedorResponsavelEmMassa(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Atenção', res.errorDescription || 'Erro ao alterar o vendedor responsável.', AlertType.warning);
          return;
        }

        if (res.qtdAlterados === 0) {
          this.alertService.show('Nada a alterar', 'Nenhuma alteração era necessária na seleção.', AlertType.info);
        } else {
          const arrastados = res.qtdSubordinadosArrastados > 0
            ? ` ${res.qtdSubordinadosArrastados} subordinado(s) acompanharam a mudança.`
            : '';
          this.alertService.show('Sucesso', `${res.qtdAlterados} vendedor(es) alterado(s).${arrastados}`, AlertType.success);
        }

        this.dialogRef.close({ success: true });
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Erro ao alterar o vendedor responsável.', AlertType.error);
      }
    });
  }

  public cancelar(): void {
    this.dialogRef.close();
  }
}
